import { Box, Typography, Paper, Button, Stack, Chip, Avatar, Tooltip } from '@mui/material';
import { OpenInNew, NotificationImportant, Info } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { CheckCircleOutlineIcon } from 'components/Icons';
import dayjs from 'dayjs';

export default function EnrollmentDetails({
    enrollment,
    daysRemaining,
    isExpired,
    isNotifiedToday,
    onNotifyParent,
    onExtendEnrollment,
    categories
}) {
    const { t } = useTranslation();
    const transferCardApproved = categories?.find(cat => cat.category === 'transfer-card')?.documents?.[0]?.status === 'approved';
    const transferCardIncluded = categories?.some(cat => cat.category === 'transfer-card');

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="h6" fontWeight="700" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {t("Enrollment Form")}
            </Typography>
            <Paper
                variant="outlined"
                sx={{
                    p: 0,
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Box
                    sx={{
                        bgcolor: 'action.hover',
                        px: 3,
                        py: 2,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <Typography variant="subtitle2" color="text.secondary" fontWeight="600">
                        {t("Enrollment Form Status")}
                    </Typography>
                    <Chip
                        label={enrollment?.finalized ? t('Submitted') : t('Not Submitted')}
                        color={enrollment?.finalized ? 'success' : 'default'}
                        size="small"
                        icon={<OpenInNew fontSize="small" />}
                        onClick={() => window.open(`/admissions/enrollment/${btoa(encodeURIComponent(enrollment.applicationSchoolId))}`, '_blank')}
                        sx={{ cursor: 'pointer' }}
                    />
                </Box>
            </Paper>

            {!enrollment?.enrollmentPackageView?.allApproved ? (
                <Paper
                    variant="outlined"
                    sx={{
                        p: 3,
                        borderRadius: 2,
                        borderStyle: 'solid',
                        borderColor: isExpired ? 'error.main' : 'warning.main',
                        bgcolor: isExpired ? 'error.lighter' : 'warning.lighter'
                    }}
                >
                    <Stack direction="row" spacing={2} alignItems="flex-start">

                        <NotificationImportant color={isExpired ? "error" : "warning"} fontSize="large" sx={{ mt: 0.5 }} />
                        <Box flex={1}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {enrollment?.finalized ? t("Documents Pending") : t("Enrollment Form Pending")}
                                </Typography>
                                {daysRemaining !== null && (
                                    <Chip
                                        label={isExpired ? t("Expired") : `${daysRemaining} ${t("days remaining")}`}
                                        color={isExpired ? "error" : "info"}
                                        size="small"
                                    />
                                )}
                            </Stack>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                {enrollment?.finalized ? t("Although the enrollment form has been submitted, some documents are still pending.") : t("The enrollment form has not been submitted yet.")}
                            </Typography>
                            {isExpired ? (
                                <Button
                                    variant="contained"
                                    color="error"
                                    size="small"
                                    onClick={onExtendEnrollment}
                                    sx={{ mt: 1, boxShadow: 'none' }}
                                >
                                    {t("Extend Enrollment")}
                                </Button>
                            ) : (
                                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" sx={{ mt: 1 }}>
                                    <Button
                                        variant="contained"
                                        color="warning"
                                        size="small"
                                        onClick={onNotifyParent}
                                        disabled={isNotifiedToday}
                                        sx={{ mt: 1, boxShadow: 'none' }}
                                    >
                                        {t("Send Reminder to Parent")}
                                    </Button>
                                    {isNotifiedToday && (
                                        <Tooltip title={t(`Parent Already Notified today (${dayjs(enrollment?.lastNotificationDate).format('MM/DD/YYYY')}).`)} placement="top">
                                            <Info color="info" fontSize="medium" sx={{ mt: 0.5 }} />
                                        </Tooltip>
                                    )}
                                </Stack>
                            )}
                        </Box>
                    </Stack>
                </Paper>
            ) : (
                <Paper
                    variant="outlined"
                    sx={{
                        p: 2.5,
                        borderRadius: 2,
                        borderColor: 'success.light',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                    }}
                >
                    <Stack direction="row" spacing={2} alignItems="center">
                        {/* Success Icon Badge */}
                        <Avatar
                            sx={{
                                bgcolor: 'success.main',
                                color: 'primary.contrastText',
                                width: 48,
                                height: 48
                            }}
                        >
                            <CheckCircleOutlineIcon fontSize="medium" />
                        </Avatar>

                        <Box flex={1}>
                            {transferCardApproved ? (
                                <>
                                    <Typography variant="h6" fontWeight="700" color="success.dark" gutterBottom={false}>
                                        {t("Enrollment Complete")}
                                    </Typography>
                                    <Typography variant="body2" color="success.dark" sx={{ opacity: 0.8 }}>
                                        {t("The student is fully enrolled and cleared for the upcoming school year.")}
                                    </Typography>
                                </>
                            ) : (
                                <>
                                    <Typography variant="h6" fontWeight="700" color="success.dark" gutterBottom={false}>
                                        {t("All Required Documents Approved")}
                                    </Typography>
                                    {transferCardIncluded ? (
                                        <Typography variant="body2" color="success.dark" sx={{ opacity: 0.8 }}>
                                            {t("The enrollment form and all required documents have been successfully verified. To complete your enrollment, the only missing document is the transfer card.")}
                                        </Typography>
                                    ) : (
                                        <Typography variant="body2" color="success.dark" sx={{ opacity: 0.8 }}>
                                            {t("All required documents have been approved. Congratulations! Your enrollment is complete.")}
                                        </Typography>
                                    )}
                                </>
                            )}
                        </Box>
                    </Stack>
                </Paper>
            )}
        </Box>
    );
}
